import { useCallback, useEffect } from 'react';

const DEV_CONSOLE_BUFFER_KEY = '__uiCliConsoleBuffer';
const DEV_CONSOLE_PATCHED_KEY = '__uiCliConsolePatched';
const DEV_CONSOLE_BUFFER_LIMIT = 800;
const DEV_NETWORK_BUFFER_KEY = '__uiCliNetworkBuffer';

const safeSerializeLogArg = (value) => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const ensureDevConsoleCapture = () => {
  if (typeof window === 'undefined') return;
  if (window[DEV_CONSOLE_PATCHED_KEY]) return;

  window[DEV_CONSOLE_BUFFER_KEY] = window[DEV_CONSOLE_BUFFER_KEY] || [];

  const levels = ['log', 'info', 'warn', 'error', 'debug'];
  levels.forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args) => {
      try {
        const entry = {
          ts: new Date().toISOString(),
          level,
          args,
          text: args.map(safeSerializeLogArg).join(' '),
        };
        window[DEV_CONSOLE_BUFFER_KEY].push(entry);
        if (window[DEV_CONSOLE_BUFFER_KEY].length > DEV_CONSOLE_BUFFER_LIMIT) {
          window[DEV_CONSOLE_BUFFER_KEY].splice(0, window[DEV_CONSOLE_BUFFER_KEY].length - DEV_CONSOLE_BUFFER_LIMIT);
        }
      } catch {
        // never block normal console behavior
      }
      original(...args);
    };
  });

  window[DEV_CONSOLE_PATCHED_KEY] = true;
};

function isLocalDevHost(hostname) {
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return true;
  if (hostname.startsWith('10.') || hostname.startsWith('192.168.')) return true;
  if (hostname.startsWith('172.')) {
    const second = Number(hostname.split('.')[1]);
    return Number.isFinite(second) && second >= 16 && second <= 31;
  }
  return false;
}

export function useDevCliBridge({
  mapRef,
  selectedArticle,
  cityName,
  historyIndex,
  historyLength,
  setIsMapCenteredOnUser,
  selectArticle,
  detailsCardRef,
  narration,
  nominatimData,
  goBack,
  goForward,
  onMapConfigured,
}) {
  useEffect(() => {
    const isDev = process.env.NODE_ENV !== 'production';
    if (isDev) ensureDevConsoleCapture();
  }, []);

  const handleDevCliCommand = useCallback(async (payload = {}) => {
    const command = String(payload.command || '').trim();
    const args = payload.args && typeof payload.args === 'object' ? payload.args : {};

    const getMapState = () => {
      const center = mapRef.current?.getCenter?.();
      return {
        center: center ? { lat: center.lat(), lng: center.lng() } : null,
        zoom: mapRef.current?.getZoom?.() ?? null,
        selectedArticleTitle: selectedArticle?.title || null,
        cityName: cityName || null,
      };
    };

    if (!command) {
      throw new Error('Missing command');
    }

    switch (command) {
      case 'ping':
        return { pong: true };

      case 'state':
        return {
          ...getMapState(),
          historyIndex,
          historyLength,
        };

      case 'map.center': {
        const lat = Number(args.lat);
        const lng = Number(args.lng);
        const zoom = args.zoom != null ? Number(args.zoom) : null;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          throw new Error('map.center requires numeric lat/lng');
        }
        if (!mapRef.current) throw new Error('Map not ready');
        mapRef.current.setCenter({ lat, lng });
        if (Number.isFinite(zoom)) mapRef.current.setZoom(zoom);
        if (typeof onMapConfigured === 'function') {
          onMapConfigured();
        } else {
          setIsMapCenteredOnUser(false);
        }
        return getMapState();
      }

      case 'map.zoom': {
        const zoom = Number(args.zoom);
        if (!Number.isFinite(zoom)) throw new Error('map.zoom requires numeric zoom');
        if (!mapRef.current) throw new Error('Map not ready');
        mapRef.current.setZoom(zoom);
        if (typeof onMapConfigured === 'function') {
          onMapConfigured();
        }
        return getMapState();
      }

      case 'topic.location':
        selectArticle(null);
        return { ok: true };

      case 'details.eras':
        if (!detailsCardRef.current?.handlePeriodsClick) throw new Error('DetailsCard not ready');
        detailsCardRef.current.handlePeriodsClick();
        return { ok: true };

      case 'details.food':
        if (!detailsCardRef.current?.handleFoodClick) throw new Error('DetailsCard not ready');
        detailsCardRef.current.handleFoodClick();
        return { ok: true };

      case 'audio.overview': {
        const title = selectedArticle?.title || cityName || nominatimData?.display_name || 'Current location';
        const topicId = selectedArticle
          ? `article:${selectedArticle.sourceWikiLang || ''}:${selectedArticle.pageid || selectedArticle.title || ''}`
          : `location:${nominatimData?.topic_wikidata || cityName || nominatimData?.display_name || title}`;
        narration.startNarration(title, 'overview', { topicId }, nominatimData, selectedArticle || null);
        return { ok: true, title, mode: 'overview' };
      }

      case 'audio.history': {
        const title = selectedArticle?.title || cityName || nominatimData?.display_name || 'Current location';
        const topicId = selectedArticle
          ? `article:${selectedArticle.sourceWikiLang || ''}:${selectedArticle.pageid || selectedArticle.title || ''}`
          : `location:${nominatimData?.topic_wikidata || cityName || nominatimData?.display_name || title}`;
        narration.startNarration(title, 'historyOverview', { topicId }, nominatimData, selectedArticle || null);
        return { ok: true, title, mode: 'historyOverview' };
      }

      case 'audio.anecdote': {
        const title = selectedArticle?.title || cityName || nominatimData?.display_name || 'Current location';
        const topicId = selectedArticle
          ? `article:${selectedArticle.sourceWikiLang || ''}:${selectedArticle.pageid || selectedArticle.title || ''}`
          : `location:${nominatimData?.topic_wikidata || cityName || nominatimData?.display_name || title}`;
        narration.startNarration(title, 'anecdote', { topicId }, nominatimData, selectedArticle || null);
        return { ok: true, title, mode: 'anecdote' };
      }

      case 'history.back':
        goBack();
        return { ok: true };

      case 'history.forward':
        goForward();
        return { ok: true };

      case 'ui.click': {
        const searchText = String(args.text || '').trim().toLowerCase();
        if (!searchText) throw new Error('ui.click requires args.text');

        const candidates = [...document.querySelectorAll('button, [role="button"]')];
        const target = candidates.find((el) => {
          const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
          if (!text || !text.includes(searchText)) return false;
          const style = window.getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') return false;
          return true;
        });

        if (!target) throw new Error(`No visible button containing "${args.text}"`);
        target.click();
        return { ok: true, clickedText: (target.textContent || '').replace(/\s+/g, ' ').trim() };
      }

      case 'ui.listButtons': {
        const filter = String(args.filter || '').trim().toLowerCase();
        const buttons = [...document.querySelectorAll('button, [role="button"]')]
          .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .filter((text) => !filter || text.toLowerCase().includes(filter));
        return { buttons: [...new Set(buttons)] };
      }

      case 'logs.tail': {
        const limitRaw = Number(args.limit);
        const limit = Number.isFinite(limitRaw) ? Math.max(1, Math.min(500, Math.floor(limitRaw))) : 80;
        const filter = String(args.filter || '').trim().toLowerCase();
        const level = String(args.level || '').trim().toLowerCase();
        const buffer = window[DEV_CONSOLE_BUFFER_KEY] || [];

        const filtered = buffer.filter((entry) => {
          if (level && entry.level !== level) return false;
          if (!filter) return true;
          return String(entry.text || '').toLowerCase().includes(filter);
        });

        return {
          total: buffer.length,
          matched: filtered.length,
          entries: filtered.slice(-limit),
        };
      }

      case 'logs.clear': {
        window[DEV_CONSOLE_BUFFER_KEY] = [];
        return { ok: true };
      }

      case 'network.tail': {
        const limitRaw = Number(args.limit);
        const limit = Number.isFinite(limitRaw) ? Math.max(1, Math.min(500, Math.floor(limitRaw))) : 80;
        const filter = String(args.filter || '').trim().toLowerCase();
        const type = String(args.type || '').trim().toLowerCase();
        const method = String(args.method || '').trim().toUpperCase();
        const statusRaw = args.status != null ? Number(args.status) : null;
        const status = Number.isFinite(statusRaw) ? statusRaw : null;

        const buffer = window[DEV_NETWORK_BUFFER_KEY] || [];
        const filtered = buffer.filter((entry) => {
          if (type && String(entry.type || '').toLowerCase() !== type) return false;
          if (method && String(entry.method || '').toUpperCase() !== method) return false;
          if (status != null && Number(entry.status) !== status) return false;
          if (!filter) return true;
          const haystack = [
            entry.url,
            entry.type,
            entry.requestBodyPreview,
            entry.responseBodyPreview,
            entry.errorMessage,
          ].filter(Boolean).join(' ').toLowerCase();
          return haystack.includes(filter);
        });

        return {
          total: buffer.length,
          matched: filtered.length,
          entries: filtered.slice(-limit),
        };
      }

      case 'network.clear': {
        window[DEV_NETWORK_BUFFER_KEY] = [];
        return { ok: true };
      }

      default:
        throw new Error(`Unknown command: ${command}`);
    }
  }, [cityName, detailsCardRef, goBack, goForward, historyIndex, historyLength, mapRef, narration, nominatimData, onMapConfigured, selectArticle, selectedArticle, setIsMapCenteredOnUser]);

  useEffect(() => {
    const isDev = process.env.NODE_ENV !== 'production';
    const isLocal = isLocalDevHost(window.location.hostname);
    if (!isDev || !isLocal) return undefined;

    const wsPort = Number(localStorage.getItem('uiCliPort') || '4317');
    let socket = null;
    let reconnectTimer = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;

      try {
        socket = new WebSocket(`ws://127.0.0.1:${wsPort}`);
      } catch (error) {
        console.warn('[UI-CLI] WebSocket connect failed:', error?.message || error);
        reconnectTimer = setTimeout(connect, 2000);
        return;
      }

      socket.onopen = () => {
        socket.send(JSON.stringify({
          type: 'hello',
          role: 'react-app',
          capabilities: [
            'state', 'map.center', 'map.zoom', 'topic.location',
            'details.eras', 'details.food', 'audio.overview', 'audio.anecdote', 'audio.history',
            'history.back', 'history.forward', 'ui.click', 'ui.listButtons',
            'logs.tail', 'logs.clear', 'network.tail', 'network.clear'
          ]
        }));
        console.log(`[UI-CLI] Connected to ws://127.0.0.1:${wsPort}`);
      };

      socket.onmessage = async (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }

        if (message?.type !== 'command') return;

        try {
          const data = await handleDevCliCommand(message);
          socket.send(JSON.stringify({ type: 'result', id: message.id, ok: true, data }));
        } catch (error) {
          socket.send(JSON.stringify({
            type: 'result',
            id: message.id,
            ok: false,
            error: error?.message || String(error),
          }));
        }
      };

      socket.onclose = () => {
        if (!stopped) {
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [handleDevCliCommand]);
}
